import { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Loader2, Sparkles, Save, Store } from "lucide-react";
import { api, apiError } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { PageHeader, money } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const emptyItem = () => ({ name: "", qty: 1, price: 0 });
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AddReceipt() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [storeName, setStoreName] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(todayISO());
  const [items, setItems] = useState([emptyItem()]);
  const [warrantyMonths, setWarrantyMonths] = useState("");
  const [payment, setPayment] = useState("");
  const [totalOverride, setTotalOverride] = useState("");
  const [photoPath, setPhotoPath] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);

  const computedTotal = useMemo(
    () => items.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.qty) || 0), 0),
    [items]
  );
  const visibleItems = useMemo(() => items.filter((i) => i.name), [items]);
  const total = totalOverride !== "" ? Number(totalOverride) || 0 : computedTotal;

  const updateItem = (idx, key, val) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  const addItem = () => setItems((a) => [...a, emptyItem()]);
  const removeItem = (idx) => setItems((a) => (a.length > 1 ? a.filter((_, i) => i !== idx) : a));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ocr/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.store_name) setStoreName(data.store_name);
      if (data.purchase_date) setPurchaseDate(String(data.purchase_date).slice(0, 10));
      if (data.items?.length) setItems(data.items.map((i) => ({ name: i.name || "", qty: i.qty || 1, price: i.price || 0 })));
      if (data.warranty_months) setWarrantyMonths(data.warranty_months);
      if (data.payment_method) setPayment(data.payment_method);
      if (data.total != null) setTotalOverride(String(data.total));
      if (data.photo_path) setPhotoPath(data.photo_path);
      toast.success("Receipt scanned — review and edit anything before saving.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!storeName.trim()) return toast.error("Add a store name.");
    setBusy(true);
    try {
      const { data } = await api.post("/receipts/personal", {
        store_name: storeName.trim(),
        items: items.filter((i) => i.name).map((i) => ({ name: i.name, qty: Number(i.qty) || 1, price: Number(i.price) || 0 })),
        total: totalOverride !== "" ? Number(totalOverride) || 0 : null,
        purchase_date: purchaseDate ? new Date(purchaseDate).toISOString() : null,
        payment_method: payment || null,
        warranty_months: warrantyMonths ? Number(warrantyMonths) : null,
        photo_path: photoPath,
      });
      qc.invalidateQueries({ queryKey: ["mine"] });
      qc.invalidateQueries({ queryKey: ["warranties"] });
      toast.success("Receipt saved to your wallet!");
      navigate(`/wallet/receipt/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppLayout>
      <PageHeader eyebrow="My wallet" title="Add a receipt"
        subtitle="Scan any receipt — photograph a paper receipt or upload a screenshot from any store, partner or not. You can edit anything before saving." />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-xl border border-dashed border-border bg-card p-6 text-center">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPhoto} data-testid="add-photo-input" />
            <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-brand-muted text-brand">
              {scanning ? <Loader2 className="h-7 w-7 animate-spin" /> : <Sparkles className="h-7 w-7" />}
            </div>
            <p className="font-heading text-lg font-bold">{scanning ? "Scanning receipt…" : "Scan with AI (optional)"}</p>
            <p className="mt-1 text-sm text-muted-foreground">Photograph a paper receipt or upload a screenshot — we'll auto-fill the details.</p>
            <Button onClick={() => fileRef.current?.click()} disabled={scanning} className="mt-4 bg-brand text-white hover:bg-brand-hover" data-testid="scan-receipt-btn">
              <Upload className="mr-2 h-4 w-4" />{photoPath ? "Rescan photo" : "Upload / photograph receipt"}
            </Button>
            {photoPath && <p className="mt-2 text-xs text-success">Photo attached ✓</p>}
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="store">Store name</Label>
                <Input id="store" value={storeName} onChange={(e) => setStoreName(e.target.value)} placeholder="e.g. JB Hi-Fi" className="mt-1.5" data-testid="store-name-field" />
              </div>
              <div>
                <Label htmlFor="date">Purchase date</Label>
                <Input id="date" type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} className="mt-1.5" data-testid="purchase-date-field" />
              </div>
              <div>
                <Label htmlFor="pay">Payment method</Label>
                <Input id="pay" value={payment} onChange={(e) => setPayment(e.target.value)} placeholder="e.g. Visa •••• 4242" className="mt-1.5" data-testid="payment-field" />
              </div>
            </div>

            <div className="mt-5 mb-3 flex items-center justify-between">
              <h3 className="font-heading font-bold">Items</h3>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn"><Plus className="mr-1 h-4 w-4" />Add item</Button>
            </div>
            <div className="space-y-3">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2" data-testid={`add-line-item-${idx}`}>
                  <Input className="col-span-6" placeholder="Item name" value={it.name} onChange={(e) => updateItem(idx, "name", e.target.value)} data-testid={`add-item-name-${idx}`} />
                  <Input className="col-span-2" type="number" min="1" placeholder="Qty" value={it.qty} onChange={(e) => updateItem(idx, "qty", e.target.value)} data-testid={`add-item-qty-${idx}`} />
                  <Input className="col-span-3" type="number" min="0" step="0.01" placeholder="Price" value={it.price} onChange={(e) => updateItem(idx, "price", e.target.value)} data-testid={`add-item-price-${idx}`} />
                  <button className="col-span-1 grid place-items-center rounded-md text-muted-foreground hover:text-destructive" onClick={() => removeItem(idx)} data-testid={`add-remove-item-${idx}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="warranty">Warranty (months)</Label>
                <Input id="warranty" type="number" min="0" value={warrantyMonths} onChange={(e) => setWarrantyMonths(e.target.value)} placeholder="e.g. 12" className="mt-1.5" data-testid="add-warranty-field" />
              </div>
              <div>
                <Label htmlFor="total">Total override (optional)</Label>
                <Input id="total" type="number" min="0" step="0.01" value={totalOverride} onChange={(e) => setTotalOverride(e.target.value)} placeholder={String(computedTotal.toFixed(2))} className="mt-1.5" data-testid="total-field" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="sticky top-24 rounded-xl border border-border bg-card p-6">
            <div className="mb-3 flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-muted text-brand"><Store className="h-4 w-4" /></span>
              <p className="truncate font-heading font-bold">{storeName || "New receipt"}</p>
            </div>
            <div className="my-4 space-y-2">
              {visibleItems.map((i, idx) => (
                <div key={`${i.name}-${idx}`} className="flex justify-between text-sm">
                  <span className="truncate text-muted-foreground">{i.qty}× {i.name}</span>
                  <span className="font-mono tabular-nums">{money((Number(i.price) || 0) * (Number(i.qty) || 0))}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-border pt-4">
              <span className="font-heading font-bold">Total</span>
              <span className="font-heading text-2xl font-black tabular-nums">{money(total)}</span>
            </div>
            <Button onClick={save} disabled={busy} className="mt-5 w-full bg-brand text-white hover:bg-brand-hover" data-testid="save-receipt-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" />Save to my wallet</>}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
